/**
 * Maintenance History Export Utilities
 * CSV and PDF/HTML export for maintenance history records
 */

import {
  downloadBlob,
  createCSVBlob,
  createHTMLBlob,
  escapeCSVField,
  arrayToCSVRow,
  generateTimestampedFilename
} from './exportHelpers';

/**
 * Format date for display
 * @param {number|string|Date} dateValue - Date to format
 * @returns {string} Formatted date string
 */
function formatDate(dateValue) {
  if (!dateValue) return '-';
  const date = typeof dateValue === 'number' ? new Date(dateValue) : new Date(dateValue);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Export maintenance history records to CSV
 * @param {Array} records - Maintenance history records
 * @param {string} vesselName - Vessel name for filename
 * @param {Object} options - Export options
 * @param {Function} options.getEquipmentName - Function to resolve equipment ID to name
 * @param {Function} options.getCrewDisplayName - Function to resolve crew ID to name
 */
export function exportMaintenanceHistoryCSV(records, vesselName, options = {}) {
  const { getEquipmentName = (id) => id, getCrewDisplayName = (id) => id } = options;

  // CSV Header
  const headers = ['Date', 'Equipment/Job', 'Type', 'Performed By', 'Result', 'Defects', 'Notes'];

  // Generate CSV content
  let csvContent = `Maintenance History Report - ${vesselName}\n`;
  csvContent += `Generated: ${new Date().toLocaleString()}\n`;
  csvContent += `Total Records: ${records.length}\n\n`;
  csvContent += arrayToCSVRow(headers) + '\n';

  // Add data rows
  records.forEach(record => {
    const recordDate = formatDate(record.completedAt || record.date);
    const title = record.title || record.checkType || record.equipmentType || 'Maintenance Check';
    const type = record.checkType || record.interval || '-';
    const performer = getCrewDisplayName(record.performedBy || record.inspector);
    const hasDefects = record.defectsFound || record.hasDefects;
    const result = hasDefects ? 'Fail' : 'Pass';
    const defects = record.linkedDefectId ? '1' : '0';
    const notes = record.notes || '';

    const row = [recordDate, title, type, performer, result, defects, notes];
    csvContent += arrayToCSVRow(row) + '\n';
  });

  // Create and download
  const blob = createCSVBlob(csvContent);
  const filename = generateTimestampedFilename(`maintenance-history-${vesselName}`, 'csv');
  downloadBlob(blob, filename);
}

/**
 * Export maintenance history records to PDF (HTML format for printing)
 * @param {Array} records - Maintenance history records
 * @param {string} vesselName - Vessel name for report header
 * @param {Object} options - Export options
 * @param {Function} options.getEquipmentName - Function to resolve equipment ID to name
 * @param {Function} options.getCrewDisplayName - Function to resolve crew ID to name
 */
export function exportMaintenanceHistoryPDF(records, vesselName, options = {}) {
  const { getEquipmentName = (id) => id, getCrewDisplayName = (id) => id } = options;

  // Generate HTML content - compact print-friendly version
  let htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Maintenance History - ${vesselName}</title>
  <style>
    /* Compact print-friendly styles */
    @page {
      size: A4;
      margin: 10mm;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 9pt;
      margin: 0;
      padding: 10px;
      color: #333;
      line-height: 1.3;
    }
    .header {
      text-align: center;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #059669;
    }
    .vessel-name {
      font-size: 14pt;
      font-weight: bold;
      margin: 0 0 4px 0;
    }
    .header h2 {
      font-size: 11pt;
      margin: 0 0 4px 0;
      font-weight: normal;
    }
    .date-range {
      font-size: 8pt;
      color: #666;
    }
    .summary-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: 8px;
      margin-bottom: 12px;
    }
    .summary-grid {
      display: flex;
      justify-content: space-around;
      text-align: center;
    }
    .summary-item {
      padding: 4px 12px;
    }
    .summary-value {
      font-size: 14pt;
      font-weight: bold;
      color: #1e293b;
    }
    .summary-label {
      font-size: 7pt;
      color: #64748b;
      text-transform: uppercase;
    }
    .section {
      margin: 8px 0;
    }
    .section-title {
      background: #f0f9ff;
      padding: 4px 8px;
      font-weight: bold;
      font-size: 10pt;
      border-left: 3px solid #059669;
      margin-bottom: 6px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8pt;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 3px 5px;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f8f9fa;
      font-weight: bold;
      font-size: 7pt;
      text-transform: uppercase;
    }
    .pass-badge {
      background: #dcfce7;
      color: #166534;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 7pt;
      font-weight: 500;
    }
    .fail-badge {
      background: #fee2e2;
      color: #991b1b;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 7pt;
      font-weight: 500;
    }
    .defect-warning {
      color: #b45309;
      font-size: 7pt;
    }
    .equipment-list {
      font-size: 7pt;
      color: #666;
      margin-top: 1px;
    }
    .footer {
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px solid #ddd;
      font-size: 7pt;
      color: #666;
    }
    .no-data {
      color: #666;
      font-style: italic;
      padding: 12px;
      text-align: center;
    }
    @media print {
      body { margin: 0; padding: 5px; }
      .section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1 class="vessel-name">${vesselName}</h1>
    <h2>Maintenance History Report</h2>
    <div class="date-range">Generated: ${new Date().toLocaleString()}</div>
  </div>
`;

  // Calculate summary stats
  const totalRecords = records.length;
  const passCount = records.filter(r => !(r.defectsFound || r.hasDefects)).length;
  const failCount = records.filter(r => r.defectsFound || r.hasDefects).length;
  const defectCount = records.filter(r => r.linkedDefectId).length;

  // Summary section
  htmlContent += `
  <div class="summary-box">
    <div class="summary-grid">
      <div class="summary-item">
        <div class="summary-value">${totalRecords}</div>
        <div class="summary-label">Total Checks</div>
      </div>
      <div class="summary-item">
        <div class="summary-value" style="color: #166534;">${passCount}</div>
        <div class="summary-label">Passed</div>
      </div>
      <div class="summary-item">
        <div class="summary-value" style="color: #991b1b;">${failCount}</div>
        <div class="summary-label">Failed</div>
      </div>
      <div class="summary-item">
        <div class="summary-value" style="color: #b45309;">${defectCount}</div>
        <div class="summary-label">Defects Raised</div>
      </div>
    </div>
  </div>
`;

  // Records table
  htmlContent += `
  <div class="section">
    <div class="section-title">Maintenance Records</div>
    <table>
      <thead>
        <tr>
          <th style="width: 12%;">Date</th>
          <th style="width: 28%;">Equipment / Job</th>
          <th style="width: 12%;">Type</th>
          <th style="width: 15%;">Performed By</th>
          <th style="width: 10%;">Result</th>
          <th style="width: 10%;">Defects</th>
          <th style="width: 13%;">Notes</th>
        </tr>
      </thead>
      <tbody>
`;

  if (records.length === 0) {
    htmlContent += `
        <tr>
          <td colspan="7" class="no-data">No maintenance records found for the selected period.</td>
        </tr>
`;
  } else {
    records.forEach(record => {
      const recordDate = formatDate(record.completedAt || record.date);
      const title = record.title || record.checkType || record.equipmentType || 'Maintenance Check';
      const type = record.checkType || record.interval || '-';
      const performer = getCrewDisplayName(record.performedBy || record.inspector);
      const hasDefects = record.defectsFound || record.hasDefects;
      const notes = record.notes || '-';

      // Get linked equipment names
      const equipmentNames = (record.equipmentIds || [])
        .map(eqId => getEquipmentName(eqId))
        .filter(name => name)
        .slice(0, 3);

      htmlContent += `
        <tr>
          <td>${recordDate}</td>
          <td>
            <strong>${title}</strong>
            ${equipmentNames.length > 0 ? `<div class="equipment-list">${equipmentNames.join(', ')}${(record.equipmentIds?.length || 0) > 3 ? ` +${record.equipmentIds.length - 3} more` : ''}</div>` : ''}
          </td>
          <td>${type}</td>
          <td>${performer}</td>
          <td><span class="${hasDefects ? 'fail-badge' : 'pass-badge'}">${hasDefects ? 'Fail' : 'Pass'}</span></td>
          <td>${record.linkedDefectId ? '<span class="defect-warning">1 raised</span>' : '-'}</td>
          <td>${notes}</td>
        </tr>
`;
    });
  }

  htmlContent += `
      </tbody>
    </table>
  </div>
`;

  // Footer
  htmlContent += `
  <div class="footer">
    <p><strong>Report generated:</strong> ${new Date().toLocaleString()}</p>
    <p><strong>Generated by:</strong> FleetSkipper</p>
    <p><em>This report provides a record of maintenance activities for compliance with WBC3 Appendix 8 / ISM Code 10.1</em></p>
  </div>
</body>
</html>
`;

  // Create and download (opens in new window for printing)
  const blob = createHTMLBlob(htmlContent);
  const url = URL.createObjectURL(blob);

  // Open in new window for print preview
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      // Auto-trigger print dialog after a short delay
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }

  // Also provide download option
  const filename = generateTimestampedFilename(`maintenance-history-${vesselName}`, 'html');
  downloadBlob(blob, filename);
}
